import { useTranslation } from "react-i18next";
import { type FormEvent, useContext, useEffect } from "react";

import {
  Flex,
  Form,
  Text,
  FormContext,
  Devider,
  Button,
  AlertsContext,
  Alerts,
  useBackendErrorMessage,
} from "dash-ui";
import pick from "lodash/pick";

import {
  GetPropertyByIdQuery,
  useSetGeneralPropertyDetailsMutation,
} from "gql/generated.graphql";
import { ChangesConfirmationModal } from "containers/properties/components/ChangesConfirmationModal";
import { compareGeneralInfoData } from "containers/properties/components/ChangesConfirmationModal/ChangesConfirmationModal.helpers";
import { TopBarWrapper } from "containers/properties/AdvancedPropertyDetails/AdvancedPropertyDetails.styles";

import {
  SaleTeams,
  NameInput,
  CityInput,
  PlaceSelect,
  StreetInput,
  StateSelect,
  CountySelect,
  ZipCodeInput,
  WebsiteInput,
  InventoryInfo,
  PriceRangeInput,
  MarketAreaSelect,
  OrganizationSelect,
  PrimaryOrganizationSelect,
} from "./components/";
import { FormFields, SALE_TEAMS_FIELDS } from "./constants/constants";
import useValidation from "./hooks/useValidation";
import { FlexWraper } from "containers/properties/PropertyGeneralInformation/components/PropertyGeneralInfoForm/PropertyGeneralInfoForm.style";

const PropertyGeneralInfoForm = ({
  property,
}: {
  property: GetPropertyByIdQuery["getPropertyById"];
}) => {
  const { t } = useTranslation(["forms", "common", "pages"]);
  const translate = useBackendErrorMessage();
  const { toggleAlert } = useContext(AlertsContext);
  const { formState, formOnSubmit, formOnError, formOnSuccess, setFormValues } =
    useContext(FormContext);
  const validateFieldByName = useValidation();
  const [setGeneralProperty, { loading }] =
    useSetGeneralPropertyDetailsMutation();

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback

    setFormValues({
      name: property.name,
      primaryOrganization: property?.primaryOrganization?._id,
      organizations: property.organizations?.map((org) => org._id),
      market: property?.marketArea?.name,
      website: property.website,
      county: property.location.address?.county,
      state: property.location.address?.state,
      city: property.location.address?.city,
      street: property.location.address?.street,
      zipCode: property.location.address?.zipCode,
      saleTeams: property.saleTeams?.length
        ? property.saleTeams.map((saleTeam) =>
            pick(saleTeam, SALE_TEAMS_FIELDS)
          )
        : [{}],
      defaultInventoryDetails: property.defaultInventoryDetails,
      mainPropertyMediaId: property.mainPropertyMediaId,
      prices: {
        min: property.priceRange?.min,
        max: property.priceRange?.max,
      },
      place: {
        place_id: property.location?.googlePlaceId,
        lat: property.location.geoLocation?.coordinates[1],
        lng: property.location.geoLocation?.coordinates[0],
      },
      averageBedroomsNumber: {
        min: property.averageBedroomsNumber?.min,
        max: property.averageBedroomsNumber?.max,
      },
      averageBathroomsNumber: {
        min: property.averageBathroomsNumber?.min,
        max: property.averageBathroomsNumber?.max,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();

    const { isLoading, errorSlug, ...formValues } = formState;

    if (
      !Object.entries(formValues).every(([name, value]: any) => {
        if (name === FormFields.County || name === FormFields.State) {
          return validateFieldByName(name, value?.name);
        }
        return validateFieldByName(name, value);
      })
    ) {
      return null;
    }

    const {
      name,
      primaryOrganization: primaryOrganizationId,
      organizations: organizationIds,
      market,
      website,
      saleTeams,
      mainPropertyMediaId,
      place,
      prices,
      defaultInventoryDetails,
      averageBedroomsNumber,
      averageBathroomsNumber,
      ...address
    } = formValues;

    setGeneralProperty({
      variables: {
        input: {
          propertyDetails: {
            propertyId: property._id,
            name,
            website,
            priceRange: prices,
            mainPropertyMediaId,
            defaultInventoryDetails,
            primaryOrganizationId,
            organizationIds,
            marketAreaId: market,
            averageBedroomsNumber: {
              min: averageBedroomsNumber?.min
                ? parseFloat(`${averageBedroomsNumber?.min}`)
                : null,
              max: averageBedroomsNumber?.max
                ? parseFloat(`${averageBedroomsNumber?.max}`)
                : null,
            },
            averageBathroomsNumber: {
              min: averageBathroomsNumber?.min
                ? parseFloat(`${averageBathroomsNumber?.min}`)
                : null,
              max: averageBathroomsNumber?.max
                ? parseFloat(`${averageBathroomsNumber?.max}`)
                : null,
            },
            location: {
              state: address.state as string,
              county: address.county as string,
              city: address.city,
              zipCode: address.zipCode,
              street: address.street,
              googlePlaceId: place.place_id,
              lat: place?.geometry?.location.lat() || place?.lat,
              lng: place?.geometry?.location.lng() || place?.lng,
            },
            saleTeams: saleTeams.reduce(
              (memo: any[], saleTeam: any) =>
                saleTeam.saleTeamId
                  ? [...memo, pick(saleTeam, SALE_TEAMS_FIELDS)]
                  : memo,
              []
            ),
          },
        },
      },
      onCompleted: () => {
        formOnSuccess();
        toggleAlert(Alerts.ChangesSaved, true);
      },
      onError: (error) => {
        formOnError(translate(error));
      },
    });
  };

  const isEqual = compareGeneralInfoData(property, formState);

  return (
    <Form onSubmit={onSubmit} w="100%">
      <ChangesConfirmationModal condition={isEqual} />
      <TopBarWrapper
        flexDirection="row"
        justifyContent="space-between"
        pos="sticky"
        alignItems="center"
        h={48}
        top={245}
      >
        <Text lg semibold>
          {t("common:labels.general")}
        </Text>
        <Button
          px={5.5}
          medium
          type="submit"
          disabled={loading || isEqual}
          isLoading={loading}
        >
          {t("common:labels.saveChanges")}
        </Button>
      </TopBarWrapper>
      <Form.Group w="100%" mt={4}>
        <NameInput disabled />
      </Form.Group>
      <Form.Group w="100%" mt={4}>
        <FlexWraper flex={1}>
          <PrimaryOrganizationSelect disabled />
          <OrganizationSelect disabled />
          <PriceRangeInput />
          <WebsiteInput disabled />
        </FlexWraper>
      </Form.Group>
      <Devider mt={6} mx={-6} />
      <InventoryInfo disabled />
      <Devider mt={6} mx={-6} mb={4} />
      <SaleTeams disabled />
      <Devider mt={6} mx={-6} />
      <Form.Group w="100%" mt={4}>
        <Text semibold lg>
          {t("common:labels.location")}
        </Text>
      </Form.Group>
      <Form.Group w="100%" mt={6}>
        <PlaceSelect
          defaultPlaceId={property.location?.googlePlaceId}
          disabled
        />
        <StreetInput disabled />
        <Flex alignItems="flex-end" flex={1}>
          <CityInput disabled />
          <ZipCodeInput disabled />
        </Flex>
      </Form.Group>
      <Form.Group w="100%" mt={4}>
        <StateSelect disabled />
        <Flex alignItems="flex-end" flex={1}>
          <CountySelect disabled />
          <MarketAreaSelect disabled />
        </Flex>
      </Form.Group>
      <Devider mt={6} mx={-6} />
    </Form>
  );
};

export default PropertyGeneralInfoForm;
